import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../supabase";

// Types
export interface FoodListing {
  id: string;
  title: string;
  description: string;
  category: string;
  quantity: string;
  location: string;
  available_until: string;
  donor_id: string;
  donor_name: string;
  donor_avatar: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
  status: "available" | "claimed" | "expired";
}

export interface FoodListingFilters {
  category?: string;
  location?: string;
  search?: string;
  status?: string;
}

// Fetch food listings with filters
const fetchFoodListings = async (
  filters: FoodListingFilters = {}
): Promise<FoodListing[]> => {
  let query = supabase
    .from("food_listings")
    .select(
      `
      *,
      donor:users!donor_id (
        name,
        profile_image_url
      )
    `
    )
    .eq("status", "available")
    .order("created_at", { ascending: false });

  // Apply filters
  if (filters.category) {
    query = query.eq("category", filters.category);
  }

  if (filters.location) {
    query = query.ilike("location", `%${filters.location}%`);
  }

  if (filters.search) {
    query = query.or(
      `title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
    );
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  // Transform the data to include donor info
  return (
    data?.map((item) => ({
      ...item,
      donor_name: item.donor?.name || "Anonymous",
      donor_avatar: item.donor?.profile_image_url || "",
    })) || []
  );
};

// Create food listing
const createFoodListing = async (
  listing: Omit<FoodListing, "id" | "created_at" | "updated_at">
) => {
  const { data, error } = await supabase
    .from("food_listings")
    .insert([listing])
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

// Update food listing
const updateFoodListing = async ({
  id,
  ...updates
}: Partial<FoodListing> & { id: string }) => {
  const { data, error } = await supabase
    .from("food_listings")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

// Delete food listing
const deleteFoodListing = async (id: string) => {
  const { error } = await supabase.from("food_listings").delete().eq("id", id);

  if (error) {
    throw new Error(error.message);
  }
};

// Custom hooks
export const useFoodListings = (filters: FoodListingFilters = {}) => {
  return useQuery({
    queryKey: ["food-listings", filters],
    queryFn: () => fetchFoodListings(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateFoodListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFoodListing,
    onSuccess: () => {
      // Invalidate and refetch food listings
      queryClient.invalidateQueries({ queryKey: ["food-listings"] });
    },
  });
};

export const useUpdateFoodListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateFoodListing,
    onSuccess: (data) => {
      // Update the cache with the new data
      queryClient.setQueryData(
        ["food-listings"],
        (old: FoodListing[] | undefined) => {
          if (!old) return [data];
          return old.map((item) => (item.id === data.id ? data : item));
        }
      );
      // Also invalidate to refetch
      queryClient.invalidateQueries({ queryKey: ["food-listings"] });
    },
  });
};

export const useDeleteFoodListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFoodListing,
    onSuccess: () => {
      // Invalidate and refetch food listings
      queryClient.invalidateQueries({ queryKey: ["food-listings"] });
    },
  });
};
